import { Link } from 'react-router-dom';
import { Heart, Check, Layers } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

// Ícone WhatsApp
const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={`${className} fill-current`}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

/**
 * ProductCard Component
 * Card de produto reutilizável para exibição em grid
 * 
 * @param {Object} product - Objeto do produto
 * @param {boolean} inCart - Se o produto está no carrinho
 * @param {Function} onCardClick - Callback ao clicar no card
 * @param {Function} onWhatsAppClick - Callback ao clicar no botão WhatsApp
 * @param {Function} onToggleWishlist - Callback ao clicar no botão de favorito
 * @param {boolean} isInWishlist - Se o produto está nos favoritos
 */
export const ProductCard = ({
  product,
  inCart = false,
  onCardClick,
  onWhatsAppClick,
  onToggleWishlist,
  isInWishlist = false
}) => {
  const hasVariations = product.variations && product.variations.length > 0;

  return (
    <Card 
      className={`group overflow-hidden rounded-2xl border transition-all hover:shadow-xl cursor-pointer ${
        inCart ? 'border-[#64BC46] bg-green-50/30' : 'border-gray-100'
      }`}
      onClick={onCardClick}
      data-testid={`product-card-${product.id}`}
    >
      <div className="aspect-square relative overflow-hidden bg-[#F3F4F6]">
        <img
          src={product.images?.[0] || 'https://placehold.co/400x400?text=Produto'}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        
        {/* Badges organizados - Mobile first */}
        <div className="absolute top-3 left-3 right-3 flex flex-col gap-2 items-start">
          {product.is_promo && (
            <Badge className="bg-[#EA1266] text-white shadow-lg text-xs px-2.5 py-0.5">
              Promoção
            </Badge>
          )}
          {hasVariations && (
            <Badge className="bg-white/95 text-[#EA1266] shadow-lg text-xs px-2.5 py-0.5 backdrop-blur-sm">
              <Layers size={11} className="mr-1" />
              {product.variations.length} {product.variations.length === 1 ? 'opção' : 'opções'}
            </Badge>
          )}
        </div>
        
        {/* Botão de Favorito */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleWishlist(product);
          }}
          className="absolute top-3 right-3 w-10 h-10 bg-white/95 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-all active:scale-95 group/heart"
          aria-label={isInWishlist ? "Remover dos favoritos" : "Adicionar aos favoritos"}
        >
          <Heart 
            size={20} 
            className={`transition-all ${
              isInWishlist 
                ? 'text-red-500 fill-red-500' 
                : 'text-gray-400 group-hover/heart:text-red-400'
            }`}
          />
        </button>
        
        {inCart && (
          <div className="absolute bottom-3 right-3 w-8 h-8 bg-[#64BC46] rounded-full flex items-center justify-center shadow-lg">
            <Check size={16} className="text-white" />
          </div>
        )}
      </div>
      
      <CardContent className="p-4 space-y-2">
        <Badge className="text-xs bg-[#0A0A0A] text-white rounded-full px-2.5 py-0.5">
          {product.brand}
        </Badge>
        <h3 className="font-medium text-sm text-[#0A0A0A] line-clamp-2 leading-snug group-hover:text-[#EA1266] transition-colors min-h-[2.5rem]">
          {product.name}
        </h3>
        {product.size && (
          <p className="text-xs text-gray-500 pb-1">{product.size}</p>
        )}
        <div className="mt-3 pt-1 flex flex-col sm:flex-row gap-2">
          <Button
            size="sm"
            className="w-full sm:flex-1 bg-[#64BC46] hover:bg-[#56a33c] text-white rounded-full text-xs h-9 font-medium"
            onClick={(e) => { 
              e.stopPropagation(); 
              onWhatsAppClick(product); 
            }}
          >
            <WhatsAppIcon className="w-4 h-4 mr-1.5" />
            Consultar
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="w-full sm:flex-1 border-[#EA1266] text-[#EA1266] hover:bg-[#EA1266] hover:text-white rounded-full text-xs h-9 font-medium"
            onClick={(e) => { 
              e.stopPropagation(); 
              onCardClick(); 
            }}
          >
            {hasVariations ? 'Ver Opções' : 'Orçamento'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
